import React from 'react'
import EastIcon from '@mui/icons-material/East'
import { alpha } from '@mui/material'
import { Box, Flex, Image, Text } from '@fundamentals'
import { localizedStrings } from '@core/strings'
import { TimelineBlockBase } from './TimelineBlockBase'
import { AuditResult } from '@core/entities/audit/AuditResult'

type ComponentProps = { auditResult: AuditResult }

export const TimelineCheckInBlock: React.FC<ComponentProps> = ({
  auditResult,
}) => {
  const { metadata, action } = auditResult

  if (action === 'PERMIT_CHECK_IN_EXTENDED') {
    return (
      <TimelineBlockBase title={localizedStrings.checkInExtended}>
        {metadata?.checkInExtensions?.map((row) => (
          <Flex key={row.checkInSessionId} alignItems='center' mb={1}>
            <Text mr={2}>{row.user?.getFullName()}</Text>
            <Text fontSize={12}>
              {row.getFormattedPreviousExpectedCheckOutOn()}
            </Text>
            <EastIcon sx={{ mx: 1, opacity: 0.5 }} />
            <Text fontSize={12}>{row.getFormattedExpectedCheckOutOn()}</Text>
          </Flex>
        ))}
      </TimelineBlockBase>
    )
  }

  if (action === 'PERMIT_FORCE_CHECKED_OUT') {
    return (
      <TimelineBlockBase title={localizedStrings.forceCheckedOut}>
        {metadata?.forcedCheckOuts?.map((row) => (
          <Text key={row.checkInSessionId} mb={0.5}>
            {row.user?.getFullName()}
          </Text>
        ))}
        {metadata?.forcedCheckOutReason && (
          <Box mt={1} pl={1.5} borderLeft='2px solid' borderColor='divider'>
            <Text fontSize={14}>{metadata.forcedCheckOutReason}</Text>
          </Box>
        )}
      </TimelineBlockBase>
    )
  }

  const checkedIn = action === 'PERMIT_CHECKED_IN'
  const rows = (checkedIn ? metadata?.checkIns : metadata?.checkOuts) ?? []
  const signatureUrls = auditResult.getCheckInSignatureUrls()
  const expectedCheckOutOn = auditResult.getFormattedCheckInExpectedCheckOutOn()
  const title = checkedIn
    ? localizedStrings.checkedInCount(rows.length)
    : localizedStrings.checkedOutCount(rows.length)

  return (
    <TimelineBlockBase>
      <Text variant='body2'>{title}</Text>
      {checkedIn && !!expectedCheckOutOn && (
        <Text fontSize={12} color='grey' sx={{ mt: 0.5 }}>
          {localizedStrings.expectedCheckOut(expectedCheckOutOn)}
        </Text>
      )}
      {rows.map((row, index) => {
        const isLast = index === rows.length - 1
        return (
          <Flex
            key={row.checkInSessionId}
            alignItems='center'
            borderBottom={isLast ? 'none' : `1px solid ${alpha('#000', 0.12)}`}
            pt={1.5}
            pb={isLast ? 0 : 1.5}
          >
            <Box flex={1}>
              <Text variant='body2' sx={{ mb: 0.5, mr: 1.5 }}>
                {row.user?.getFullName()}
              </Text>
              <Text fontSize={12} color='grey'>
                {row.user?.company}
              </Text>
            </Box>
            {!!signatureUrls[index] && (
              <Image
                style={{ height: 40, objectFit: 'contain' }}
                src={signatureUrls[index]}
              />
            )}
          </Flex>
        )
      })}
    </TimelineBlockBase>
  )
}
